import { TreeCheckboxBaseNode, TreeItemsMap } from './types';

export function getItemById<T extends TreeCheckboxBaseNode>(id: string, treeMap: TreeItemsMap<T>): T | null {
  return treeMap.itemMap.get(id) || null;
}

export function createTreeItemsMap<T extends TreeCheckboxBaseNode>(items: T[]): TreeItemsMap<T> {
  const itemMap = new Map<string, T>();
  const stack = [...items];
  while (stack.length > 0) {
    const item = stack.pop()!;
    itemMap.set(item.id, item);
    if (item.children) {
      stack.push(...(item.children as T[]));
    }
  }
  return { itemMap };
}

/**
 * item의 모든 하위 노드들 중 disabled가 아닌 노드들의 ID를 배열로 반환(본인 제외)
 */
function getAllEnabledDescendantIds<T extends TreeCheckboxBaseNode>(item: T): string[] {
  const descendants: string[] = [];
  const stack = item.children ? [...(item.children as T[])] : [];
  while (stack.length > 0) {
    const currentItem = stack.pop()!;
    if (!currentItem.disabled) {
      descendants.push(currentItem.id);
      if (currentItem.children) {
        stack.push(...(currentItem.children as T[]));
      }
    }
  }
  return descendants;
}

/**
 * ID 배열을 실제 아이템 객체 배열로 변환
 */
export function convertIdsToItems<T extends TreeCheckboxBaseNode>(ids: string[], itemsMap: TreeItemsMap<T>): T[] {
  const items: T[] = [];
  const addedIds = new Set<string>();
  ids.forEach((id) => {
    if (addedIds.has(id)) return;
    const foundItem = getItemById(id, itemsMap);
    if (foundItem) {
      items.push(foundItem);
      addedIds.add(id);
    }
  });
  return items;
}

/**
 * 트리 노드 캐싱 유틸리티
 */
export function createOptimizedUtils<T extends TreeCheckboxBaseNode>(treeMap: TreeItemsMap<T>) {
  const enabledDescendantCache = new Map<string, string[]>();
  const ancestorCache = new Map<string, Set<string>>();

  const getEnabledDescendants = (item: T): string[] => {
    if (!enabledDescendantCache.has(item.id)) {
      enabledDescendantCache.set(item.id, getAllEnabledDescendantIds(item));
    }
    return enabledDescendantCache.get(item.id)!;
  };

  const hasDescendant = (parent: T, targetId: string): boolean => {
    return getEnabledDescendants(parent).includes(targetId);
  };

  const getAncestors = (targetId: string): Set<string> => {
    if (!ancestorCache.has(targetId)) {
      const ancestors = new Set<string>();
      treeMap.itemMap.forEach((candidate) => {
        if (candidate.children && hasDescendant(candidate, targetId)) {
          ancestors.add(candidate.id);
        }
      });
      ancestorCache.set(targetId, ancestors);
    }
    return ancestorCache.get(targetId)!;
  };

  return { getEnabledDescendants, hasDescendant, getAncestors };
}

/**
 * 최종 아이템별 checked/indeterminate 상태 확인용 함수
 * disabled 요소는 체크 상태 계산에서 제외됨
 */
export function getItemCheckedState<T extends TreeCheckboxBaseNode>(
  item: T,
  checkedIds: Set<string>,
): { checked: boolean; indeterminate: boolean } {
  if (checkedIds.has(item.id)) {
    return { checked: true, indeterminate: false };
  }
  if (!item.children || item.children.length === 0) {
    return { checked: false, indeterminate: false };
  }

  const enabledChildren = item.children.filter((child) => !(child as T).disabled);

  // disabled가 아닌 자식이 없으면 unchecked
  if (enabledChildren.length === 0) {
    return { checked: false, indeterminate: false };
  }

  const childStates = enabledChildren.map((child) => getItemCheckedState(child as T, checkedIds));
  const checkedChildrenCount = childStates.filter((state) => state.checked).length;
  const indeterminateChildrenCount = childStates.filter((state) => state.indeterminate).length;

  // disabled가 아닌 모든 자식이 체크되었는지 확인
  const allEnabledChildrenChecked = checkedChildrenCount === enabledChildren.length;
  const someChildrenChecked = checkedChildrenCount > 0 || indeterminateChildrenCount > 0;

  if (allEnabledChildrenChecked) {
    return { checked: true, indeterminate: false };
  } else if (someChildrenChecked) {
    return { checked: false, indeterminate: true };
  } else {
    return { checked: false, indeterminate: false };
  }
}
